import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { buildHotspotLoginUrl } from '../../utils/voucher_qr';
import { formatVoucherDuration } from './durationUtil';

/**
 * DenseGridTemplate
 * Mendukung kepadatan tinggi: 50 (10×5) atau 70 (10×7) voucher per halaman A4 landscape.
 * Props:
 *  - vouchers: array
 *  - companyLogo: string (data URL or url)
 *  - companyName: string
 *  - density: 50 | 70 (default 50)
 */
const DenseGridTemplate = ({ vouchers, companyLogo = '', companyName = '', density = 50, hotspotGatewayIp, batchHeader}) => {
  const cols = 10;
  const rows = density === 100 ? 10 : (density === 70 ? 7 : 5);
  // COMPACT_DENSE70_V2 + DENSE100_V1
  const cardH = density === 100 ? 19.8 : (density === 70 ? 29 : 40); // mm
  const cls = density === 100 ? 'dense100' : (density === 70 ? 'dense70' : 'dense50');

  return (
    <div className={`print-container-${cls} print-root`}>
      <style jsx>{`
        @page {
          size: 297mm 210mm;
          size: A4 landscape;
          margin: 0;
        }
        @media print {
          html, body { width: 100%; height: 100%; margin: 0 !important; padding: 0 !important; background: white !important; }
          html *:not(.print-root):not(.print-root *) { visibility: hidden !important; }
          .print-root, .print-root * { visibility: visible !important; }
          .print-root {
            position: absolute !important;
            left: 0 !important; top: 0 !important;
            transform: none !important;
            width: 100% !important;
          }
        }
        .print-container-${cls} {
          width: 297mm;
          min-height: 210mm;
          padding: 0;
          margin: 0;
          background: white;
          box-sizing: border-box;
          font-family: 'Arial', sans-serif;
        }
        .${cls}-grid {
          display: grid;
          grid-template-columns: repeat(${cols}, 1fr);
          grid-auto-rows: ${cardH}mm;
          gap: ${density === 100 ? '0.3mm' : '0.5mm'};
        }
        .${cls}-card {
          border: ${density === 100 ? '0.5px solid #1f2937' : '0.6px solid #d97706'};
          border-radius: 2mm;
          background: white;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          box-sizing: border-box;
          position: relative;
        }
        .${cls}-no {
          background: ${density === 100 ? 'transparent' : '#fff7ed'};
          color: ${density === 100 ? '#6b7280' : '#92400e'};
          font-size: ${density === 100 ? '3.5pt' : (density === 70 ? '4.5pt' : '5pt')};
          font-weight: 700;
          padding: ${density === 100 ? '0' : '0.1mm 1mm 0 1mm'};
          line-height: 1.05;
          border-bottom: ${density === 100 ? 'none' : '0.4px solid #fed7aa'};
          position: ${density === 100 ? 'absolute' : 'static'};
          top: ${density === 100 ? '0.3mm' : 'auto'};
          right: ${density === 100 ? '0.8mm' : 'auto'};
          z-index: ${density === 100 ? '2' : 'auto'};
        }
        .${cls}-brand {
          background: ${density === 100 ? 'transparent' : '#f97316'};
          color: ${density === 100 ? '#111827' : 'white'};
          padding: ${density === 100 ? '0.3mm' : (density === 70 ? '0.2mm' : '0.6mm')};
          text-align: center;
          font-size: ${density === 100 ? '4pt' : (density === 70 ? '5pt' : '6pt')};
          font-weight: 800;
          letter-spacing: 0.2mm;
        }
        .${cls}-brand-logo {
          max-height: ${density === 100 ? '2.4mm' : (density === 70 ? '3mm' : '4mm')};
          max-width: 100%;
          object-fit: contain;
          display: block;
          margin: 0 auto;
        }
        .${cls}-price {
          background: white;
          color: ${density === 100 ? '#111827' : '#1d4ed8'};
          text-align: center;
          font-size: ${density === 100 ? '5.5pt' : (density === 70 ? '7pt' : '9pt')};
          font-weight: 900;
          padding: ${density === 100 ? '0.1mm' : (density === 70 ? '0.2mm' : '0.5mm')};
          border-top: ${density === 100 ? '0.4px dashed #9ca3af' : '0.4px dashed #fdba74'};
        }
        .${cls}-body {
          flex: 1;
          display: flex;
          align-items: center;
          padding: 0.3mm 1mm;
          gap: 0.8mm;
        }
        .${cls}-code-wrap {
          flex: 1;
          min-width: 0;
        }
        .${cls}-code-label {
          font-size: ${density === 100 ? '2.8pt' : (density === 70 ? '3.5pt' : '4pt')};
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.2mm;
          margin-bottom: 0.3mm;
        }
        .${cls}-code {
          font-family: 'Courier New', monospace;
          font-size: ${density === 100 ? '4.5pt' : (density === 70 ? '6pt' : '7.5pt')};
          font-weight: 900;
          color: ${density === 100 ? '#111827' : '#1d4ed8'};
          word-break: break-all;
          line-height: 1;
        }
        .${cls}-qr {
          flex-shrink: 0;
        }
        .${cls}-footer {
          background: #f97316;
          color: white;
          text-align: center;
          font-size: ${density === 100 ? '3.2pt' : (density === 70 ? '3.5pt' : '4pt')};
          font-weight: 700;
          padding: ${density === 100 ? '0.3mm 0.1mm 0.3mm 0.1mm' : (density === 70 ? '0.2mm' : '0mm 0.2mm 1.4mm 0.2mm')};
          letter-spacing: 0.2mm;
          line-height: 1;
          transform: ${density === 100 ? 'none' : (density === 70 ? 'none' : 'translateY(-4px)')};
        }
        .${cls}-validity {
          font-size: ${density === 100 ? '3pt' : '0'};
          color: #374151;
          font-weight: 700;
          margin-top: 0.4mm;
          letter-spacing: 0.1mm;
          display: ${density === 100 ? 'block' : 'none'};
          line-height: 1;
        }
      `}</style>

      {batchHeader}
      <div className={`${cls}-grid`}>
      {vouchers.map((voucher, index) => {
        const qrSize = density === 100 ? 18 : (density === 70 ? 22 : 36);
        return (
          <div key={voucher.id || index} data-voucher-card className={`${cls}-card`}>
            <div className={`${cls}-no`}>#{String(index + 1).padStart(2, '0')}</div>
            <div className={`${cls}-brand`}>
              {companyLogo ? (
                <img src={companyLogo} alt={companyName || 'Logo'} className={`${cls}-brand-logo`} />
              ) : (
                <span>{companyName || 'ISP Provider'}</span>
              )}
            </div>
            <div className={`${cls}-price`}>
              Rp. {(voucher.price || 0).toLocaleString('id-ID')}
            </div>
            <div className={`${cls}-body`}>
              <div className={`${cls}-code-wrap`}>
                <div className={`${cls}-code-label`}>Kode</div>
                <div className={`${cls}-code`}>{voucher.code || voucher.username}</div>
                {density === 100 && (
                  <div className={`${cls}-validity`}>
                    Aktif: {formatVoucherDuration(voucher)}
                  </div>
                )}
              </div>
              <div className={`${cls}-qr`}>
                <QRCodeSVG value={buildHotspotLoginUrl(voucher.username || voucher.code || '', voucher.password || voucher.code || '', hotspotGatewayIp)} size={qrSize} level="L" />
              </div>
            </div>
            {density !== 100 && (
              <div className={`${cls}-footer`}>
                {formatVoucherDuration(voucher)}
              </div>
            )}
          </div>
        );
      })}
      </div>
    </div>
  );
};

export default DenseGridTemplate;
